import { useRef, useEffect, useState, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import GpsScorecard from '@/components/GpsScorecard';
import { saveLocalCourse, captureHoleData, normalizeCourse, suggestClubFromBag } from '@/lib/golfDataService';
import {
  Crosshair, CaretLeft, CaretRight, MapPin, MagnifyingGlass,
  NavigationArrow, Golf, Star, MapTrifold, ArrowLeft, Flag,
  ListBullets, Target, Path
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

function yardsFromCoords(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(R * c * 1.09361);
}

function suggestClub(yards) {
  if (!yards || yards <= 0) return null;
  const bagClub = suggestClubFromBag(yards);
  if (bagClub) return bagClub.abbr;
  // Fallback
  if (yards <= 30) return 'P';
  if (yards <= 60) return 'SW';
  if (yards <= 80) return 'LW';
  if (yards <= 110) return 'PW';
  if (yards <= 130) return '9i';
  if (yards <= 145) return '8i';
  if (yards <= 160) return '7i';
  if (yards <= 175) return '6i';
  if (yards <= 190) return '5i';
  if (yards <= 210) return '4i';
  if (yards <= 230) return '3W';
  return 'DR';
}

// Dynamic club suggestion using tracked shot stats (Phase C)
function suggestClubDynamic(yards, stats) {
  if (!yards || yards <= 0 || !stats || Object.keys(stats).length === 0) return null;
  let best = null;
  let bestDiff = Infinity;
  for (const [club, data] of Object.entries(stats)) {
    if (data.count >= 3 && data.avg > 0) {
      const diff = Math.abs(data.avg - yards);
      if (diff < bestDiff) { bestDiff = diff; best = { abbr: club, avg: data.avg, count: data.count }; }
    }
  }
  return best;
}

// Generate GeoJSON circle for dispersion ellipse overlay
function createDispersionCircle(centerLat, centerLng, radiusYards) {
  if (!centerLat || !centerLng || !radiusYards || radiusYards <= 0) return null;
  const radiusMeters = radiusYards * 0.9144;
  const points = 32;
  const coords = [];
  for (let i = 0; i <= points; i++) {
    const angle = (i / points) * 2 * Math.PI;
    const dx = radiusMeters * Math.cos(angle);
    const dy = radiusMeters * Math.sin(angle);
    const lat = centerLat + (dy / 111320);
    const lng = centerLng + (dx / (111320 * Math.cos(centerLat * Math.PI / 180)));
    coords.push([lng, lat]);
  }
  return { type: 'Feature', geometry: { type: 'Polygon', coordinates: [coords] } };
}

function CourseCard({ course, index, onSelect }) {
  return (
    <button
      onClick={() => onSelect(course)}
      className="w-full flex items-stretch gap-0 bg-white border border-border rounded-sm overflow-hidden hover:border-primary/30 hover:shadow-md transition-all duration-200 group text-left"
      data-testid={`course-card-${index}`}
    >
      {/* Left accent strip */}
      <div className="w-1.5 bg-primary flex-shrink-0 group-hover:bg-[#D4AF37] transition-colors" />

      {/* Course info */}
      <div className="flex-1 p-4 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="font-sans text-sm font-semibold text-foreground truncate group-hover:text-primary transition-colors" data-testid={`course-name-${index}`}>
              {course.clubName}
            </h3>
            <p className="text-xs text-muted-foreground font-sans mt-0.5 truncate">
              {course.courseName}
            </p>
          </div>
          <div className="flex-shrink-0 text-right">
            <p className="text-sm font-semibold text-primary font-sans" data-testid={`course-distance-${index}`}>
              {course.distance < 1 ? `${(course.distance * 5280).toFixed(0)} ft` : `${course.distance.toFixed(1)} mi`}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 mt-2.5">
          <span className="inline-flex items-center gap-1 text-[10px] font-sans text-muted-foreground bg-muted px-2 py-0.5 rounded-sm">
            <Flag size={10} weight="fill" />
            {course.numHoles} Holes
          </span>
          {course.hasGPS ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-sans text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-sm">
              <NavigationArrow size={10} weight="fill" />
              GPS
            </span>
          ) : null}
          <span className="text-[10px] font-sans text-muted-foreground">
            {course.city}, {course.state}
          </span>
        </div>

        {course.address && (
          <p className="text-[10px] text-muted-foreground/60 font-sans mt-1.5 truncate">
            {course.address}
          </p>
        )}
      </div>

      {/* Right arrow */}
      <div className="flex items-center px-3 text-muted-foreground group-hover:text-primary transition-colors">
        <CaretRight size={16} />
      </div>
    </button>
  );
}

function CourseDiscovery({ userPos, onSelectCourse, savedCourses, onStartRound }) {
  const [nearbyCourses, setNearbyCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Fetch nearby from GolfAPI (primary — unchanged)
  useEffect(() => {
    if (!userPos) { setLoading(false); return; }
    api.get(`/golf/nearby-courses?lat=${userPos[1]}&lng=${userPos[0]}&limit=15`)
      .then(({ data }) => {
        setNearbyCourses(data.courses || []);
        // Silent capture: save to local DB
        (data.courses || []).forEach(c => saveLocalCourse(normalizeCourse(c, 'golfapi')));
      })
      .catch(() => toast.error('Could not load nearby courses'))
      .finally(() => setLoading(false));
  }, [userPos]);

  // OpenGolfAPI search (parallel — extends search beyond GolfAPI)
  useEffect(() => {
    if (!searchQuery.trim() || searchQuery.length < 3) { setSearchResults([]); return; }
    const timer = setTimeout(() => {
      setSearching(true);
      api.get(`/golf/opengolf/search?q=${encodeURIComponent(searchQuery)}`)
        .then(({ data }) => {
          const normalized = (data.courses || []).map(c => normalizeCourse(c, 'opengolf'));
          setSearchResults(normalized);
        })
        .catch(() => {})
        .finally(() => setSearching(false));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const filtered = searchQuery.trim()
    ? nearbyCourses.filter(c =>
        c.clubName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.courseName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.city?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : nearbyCourses;

  // Merge GolfAPI filtered results with OpenGolfAPI results (deduped)
  const mergedResults = searchQuery.trim() ? (() => {
    const golfApiResults = filtered;
    const seen = new Set(golfApiResults.map(c => (c.clubName || '').toLowerCase()));
    const extra = searchResults.filter(c => !seen.has((c.clubName || '').toLowerCase()));
    return [...golfApiResults, ...extra];
  })() : filtered;

  return (
    <div className="min-h-screen bg-background" data-testid="course-discovery">
      {/* Hero header */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0B421A 0%, #1B4332 40%, #143728 100%)' }}>
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23D4AF37' fill-opacity='0.3'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />

        <div className="relative px-5 pt-8 pb-6 lg:px-8 lg:pt-12 lg:pb-8 max-w-2xl mx-auto">
          <div className="flex items-center gap-2 mb-1">
            <Golf size={20} weight="fill" className="text-[#D4AF37]" />
            <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.2em] font-sans font-semibold">Play</p>
          </div>
          <h1 className="font-serif text-2xl lg:text-3xl text-white font-semibold tracking-tight" data-testid="play-header-title">
            Find Your Course
          </h1>
          <p className="text-white/60 text-sm font-sans mt-1">Discover courses near you and start your round</p>

          {/* Search bar */}
          <div className="mt-5 relative" data-testid="course-search-bar">
            <MagnifyingGlass size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search courses by name or city..."
              className="w-full bg-white/10 border border-white/15 text-white placeholder-white/40 text-sm font-sans rounded-sm pl-10 pr-4 py-3 focus:outline-none focus:border-[#D4AF37]/50 focus:bg-white/15 transition-colors"
              data-testid="course-search-input"
            />
          </div>
        </div>
      </div>

      {/* Course list */}
      <div className="max-w-2xl mx-auto px-4 lg:px-6 py-5">
        {/* Saved courses section */}
        {savedCourses.length > 0 && !searchQuery && (
          <div className="mb-6">
            <p className="text-[10px] text-muted-foreground uppercase tracking-[0.15em] font-sans font-semibold mb-3">
              Your Courses
            </p>
            <div className="space-y-2">
              {savedCourses.map((c, i) => (
                <button
                  key={c.course_id}
                  onClick={() => onStartRound(c)}
                  className="w-full flex items-center gap-3 bg-primary/5 border border-primary/10 rounded-sm p-3 hover:bg-primary/10 transition-colors text-left group"
                  data-testid={`saved-course-${i}`}
                >
                  <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Flag size={18} weight="fill" className="text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-sans text-sm font-semibold text-foreground truncate">{c.name}</p>
                    <p className="text-[10px] text-muted-foreground font-sans">{c.holes?.length || 0} holes configured</p>
                  </div>
                  <span className="text-xs font-sans font-semibold text-primary group-hover:text-[#D4AF37] transition-colors">
                    Play
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Nearby courses */}
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] text-muted-foreground uppercase tracking-[0.15em] font-sans font-semibold">
            {searchQuery ? `Results for "${searchQuery}"` : 'Nearest Courses'}
          </p>
          {!loading && (
            <p className="text-[10px] text-muted-foreground font-sans">
              {filtered.length} course{filtered.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>

        {loading ? (
          <div className="space-y-3" data-testid="courses-loading">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-[88px] bg-muted/50 rounded-sm animate-pulse" />
            ))}
          </div>
        ) : mergedResults.length === 0 ? (
          <div className="text-center py-12" data-testid="no-courses">
            <Golf size={36} weight="thin" className="mx-auto text-muted-foreground/40" />
            <p className="text-muted-foreground text-sm font-sans mt-3 font-medium">
              {searchQuery ? 'No matching courses' : 'No courses found nearby'}
            </p>
            {searchQuery && searching && (
              <p className="text-muted-foreground/50 text-xs font-sans mt-1">Searching more sources...</p>
            )}
          </div>
        ) : (
          <div className="space-y-2" data-testid="nearby-courses-list">
            {mergedResults.map((course, i) => (
              course.source === 'opengolf' ? (
                <button
                  key={`og-${course.id}-${i}`}
                  onClick={() => onSelectCourse(course)}
                  className="w-full flex items-stretch gap-0 bg-white border border-border rounded-sm overflow-hidden hover:border-primary/30 hover:shadow-md transition-all duration-200 group text-left"
                  data-testid={`course-card-${i}`}
                >
                  <div className="w-1.5 bg-blue-500 flex-shrink-0 group-hover:bg-[#D4AF37] transition-colors" />
                  <div className="flex-1 p-4 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <h3 className="font-sans text-sm font-semibold text-foreground truncate">{course.clubName}</h3>
                        <p className="text-xs text-muted-foreground font-sans mt-0.5 truncate">{course.courseName}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 mt-2.5">
                      <span className="inline-flex items-center gap-1 text-[10px] font-sans text-blue-600 bg-blue-50 px-2 py-0.5 rounded-sm">
                        OpenGolf
                      </span>
                      <span className="inline-flex items-center gap-1 text-[10px] font-sans text-muted-foreground">
                        <Flag size={10} weight="fill" /> {course.numHoles} Holes
                      </span>
                      <span className="text-[10px] font-sans text-muted-foreground">{course.city}, {course.state}</span>
                    </div>
                  </div>
                  <div className="flex items-center px-3 text-muted-foreground group-hover:text-primary transition-colors">
                    <CaretRight size={16} />
                  </div>
                </button>
              ) : (
                <CourseCard key={`${course.courseID}-${i}`} course={course} index={i} onSelect={onSelectCourse} />
              )
            ))}
          </div>
        )}
        {/* Course not found? Add it */}
        <div className="mt-6 pt-4 border-t border-border text-center">
          <p className="text-muted-foreground text-xs font-sans mb-2">Course not listed?</p>
          <button
            onClick={() => onSelectCourse({ source: 'pindrop' })}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-sans font-semibold text-[#1B4332] border border-[#1B4332]/30 rounded-sm hover:bg-[#1B4332]/5 transition-colors"
            data-testid="add-course-btn"
          >
            <MapPin size={16} weight="fill" /> Add Your Course
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PlayPage() {
  const { user } = useAuth();
  const mapContainer = useRef(null);
  const map = useRef(null);
  const userMarker = useRef(null);
  const greenMarkers = useRef([]);
  const [savedCourses, setSavedCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [currentHole, setCurrentHole] = useState(0);
  const [userPos, setUserPos] = useState(null);
  const [distances, setDistances] = useState({ front: null, center: null, back: null });
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('discovery'); // 'discovery' | 'detail' | 'setup' | 'pindrop' | 'gps'
  const [selectedNearbyCourse, setSelectedNearbyCourse] = useState(null);
  const [showScorecard, setShowScorecard] = useState(false);
  const [trackedShots, setTrackedShots] = useState([]);
  const [trackingShot, setTrackingShot] = useState(false);
  const [sessionId] = useState(() => `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  const shotMarkers = useRef([]);
  // New state for play flow
  const [courseDetail, setCourseDetail] = useState(null);
  const [roundType, setRoundType] = useState('18'); // '18' | 'front9' | 'back9'
  const [players, setPlayers] = useState([{ name: '', handicap: '' }]);
  // Pin-drop course builder
  const [customHoles, setCustomHoles] = useState([]);
  const [customCourseName, setCustomCourseName] = useState('');
  // Phase C: dispersion + dynamic club stats
  const [clubStats, setClubStats] = useState({});

  // Get user location
  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (pos) => setUserPos([pos.coords.longitude, pos.coords.latitude]),
      () => {
        setUserPos([-82.02, 33.50]);
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  }, []);

  // Fetch saved courses
  useEffect(() => {
    api.get('/courses?limit=100')
      .then(({ data }) => {
        setSavedCourses(data.courses || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // Fetch club stats for dynamic recommendation (Phase C)
  useEffect(() => {
    if (mode !== 'gps') return;
    api.get('/golf/club-stats')
      .then(({ data }) => setClubStats(data.stats || {}))
      .catch(() => {});
  }, [mode]);

  // Start GPS round for a saved course
  const startRound = useCallback((course) => {
    setSelectedCourse(course);
    setCurrentHole(roundType === 'back9' ? 9 : 0);
    setMode('gps');
  }, [roundType]);

  // When user selects a nearby course from discovery — go to detail or pindrop
  const handleSelectNearbyCourse = useCallback((course) => {
    if (course.source === 'pindrop') {
      setMode('pindrop');
      setCustomHoles([]);
      setCustomCourseName('');
      return;
    }
    setSelectedNearbyCourse(course);
    setCourseDetail(null);
    setMode('detail');
    if (course.source === 'opengolf' && course.id) {
      api.get(`/golf/opengolf/course/${course.id}`)
        .then(({ data }) => setCourseDetail(data))
        .catch(() => {});
    }
  }, []);

  // From detail → setup
  const goToSetup = useCallback(() => {
    setMode('setup');
  }, []);

  // From setup → GPS
  const startPlayRound = useCallback(() => {
    setCurrentHole(roundType === 'back9' ? 9 : 0);
    setMode('gps');
  }, [roundType]);

  // Back to discovery
  const backToDiscovery = useCallback(() => {
    setMode('discovery');
    setSelectedNearbyCourse(null);
    setCourseDetail(null);
    setShowScorecard(false);
    setTrackedShots([]);
    setTrackingShot(false);
    setPlayers([{ name: '', handicap: '' }]);
    shotMarkers.current.forEach(m => m.remove());
    shotMarkers.current = [];
    if (map.current) {
      map.current.remove();
      map.current = null;
    }
    userMarker.current = null;
    greenMarkers.current = [];
  }, []);

  // GPS map initialization (only in gps mode)
  useEffect(() => {
    if (mode !== 'gps' || !mapContainer.current || map.current) return;

    mapboxgl.accessToken = MAPBOX_TOKEN;
    const center = userPos || [-80.1, 26.7];
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/satellite-streets-v12',
      center,
      zoom: 17,
      pitch: 45,
    });
    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
    map.current.on('load', () => { map.current.resize(); });

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const coords = [pos.coords.longitude, pos.coords.latitude];
        setUserPos(coords);
        if (!userMarker.current && map.current) {
          const el = document.createElement('div');
          el.className = 'gps-user-marker';
          el.innerHTML = '<div class="gps-pulse"></div><div class="gps-dot"></div>';
          userMarker.current = new mapboxgl.Marker(el).setLngLat(coords).addTo(map.current);
          map.current.flyTo({ center: coords, zoom: 17 });
        } else if (userMarker.current) {
          userMarker.current.setLngLat(coords);
        }
      },
      () => toast.error('GPS not available'),
      { enableHighAccuracy: true, maximumAge: 5000 }
    );

    return () => {
      navigator.geolocation.clearWatch(watchId);
      map.current?.remove();
      map.current = null;
      userMarker.current = null;
    };
  }, [mode]);

  const hole = selectedCourse?.holes?.[currentHole];

  // Draw green markers
  useEffect(() => {
    if (!map.current || !hole) return;
    greenMarkers.current.forEach(m => m.remove());
    greenMarkers.current = [];

    const points = [
      { lat: hole.green_center_lat, lng: hole.green_center_lng, label: 'C', color: '#D4AF37' },
      { lat: hole.green_front_lat, lng: hole.green_front_lng, label: 'F', color: '#22c55e' },
      { lat: hole.green_back_lat, lng: hole.green_back_lng, label: 'B', color: '#ef4444' },
      { lat: hole.tee_lat, lng: hole.tee_lng, label: 'T', color: '#ffffff' },
    ];

    let bounds = null;
    points.forEach(({ lat, lng, label, color }) => {
      if (lat && lng) {
        const el = document.createElement('div');
        el.style.cssText = `width:24px;height:24px;border-radius:50%;background:${color};border:2px solid white;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:700;color:${color === '#ffffff' ? '#1B4332' : 'white'};box-shadow:0 2px 8px rgba(0,0,0,0.4)`;
        el.textContent = label;
        const marker = new mapboxgl.Marker(el).setLngLat([lng, lat]).addTo(map.current);
        greenMarkers.current.push(marker);
        if (!bounds) bounds = new mapboxgl.LngLatBounds([lng, lat], [lng, lat]);
        else bounds.extend([lng, lat]);
      }
    });

    if (userPos) {
      if (!bounds) bounds = new mapboxgl.LngLatBounds(userPos, userPos);
      else bounds.extend(userPos);
    }
    if (bounds) map.current.fitBounds(bounds, { padding: 80, maxZoom: 18 });
  }, [hole, userPos]);

  // Phase C: Club suggestion computed early for dispersion useEffect
  const clubSuggestionForDispersion = suggestClub(distances.center);

  // Phase C: Dispersion ellipse overlay on map
  useEffect(() => {
    if (!map.current || !userPos || !distances.center || !clubSuggestionForDispersion) return;
    const m = map.current;
    // Find dispersion data for the suggested club
    const stats = clubStats[clubSuggestionForDispersion];
    const radiusYards = stats?.avg ? Math.round((stats.avg - (stats.avg * 0.85)) * 0.5) : null;
    if (!radiusYards || radiusYards < 5) {
      // Remove existing dispersion layer if no data
      try { if (m.getLayer('dispersion-fill')) m.removeLayer('dispersion-fill'); } catch {}
      try { if (m.getLayer('dispersion-outline')) m.removeLayer('dispersion-outline'); } catch {}
      try { if (m.getSource('dispersion')) m.removeSource('dispersion'); } catch {}
      return;
    }
    // Build ellipse centered on green
    const greenLat = hole?.green_center_lat;
    const greenLng = hole?.green_center_lng;
    if (!greenLat || !greenLng) return;
    const geojson = createDispersionCircle(greenLat, greenLng, radiusYards);
    if (!geojson) return;
    try {
      if (m.getSource('dispersion')) {
        m.getSource('dispersion').setData(geojson);
      } else {
        m.addSource('dispersion', { type: 'geojson', data: geojson });
        m.addLayer({ id: 'dispersion-fill', type: 'fill', source: 'dispersion', paint: { 'fill-color': '#D4AF37', 'fill-opacity': 0.15 } });
        m.addLayer({ id: 'dispersion-outline', type: 'line', source: 'dispersion', paint: { 'line-color': '#D4AF37', 'line-width': 1.5, 'line-opacity': 0.4 } });
      }
    } catch {}
  }, [distances.center, clubSuggestionForDispersion, clubStats, hole, userPos]);

  // Calculate distances (current position to green AND tee to green)
  useEffect(() => {
    if (!hole) { setDistances({ front: null, center: null, back: null }); return; }
    const calcFrom = (fromLat, fromLng, toLat, toLng) => (fromLat && fromLng && toLat && toLng) ? yardsFromCoords(fromLat, fromLng, toLat, toLng) : null;

    if (userPos) {
      setDistances({
        front: calcFrom(userPos[1], userPos[0], hole.green_front_lat, hole.green_front_lng),
        center: calcFrom(userPos[1], userPos[0], hole.green_center_lat, hole.green_center_lng),
        back: calcFrom(userPos[1], userPos[0], hole.green_back_lat, hole.green_back_lng),
      });

      // Silent data capture: store GPS sample for this hole
      const courseId = selectedCourse?.course_id || selectedNearbyCourse?.courseID || selectedNearbyCourse?.id;
      if (courseId) {
        captureHoleData(courseId, currentHole + 1, { lat: userPos[1], lng: userPos[0] });
      }
    } else {
      // Fallback: show tee-to-green distances
      setDistances({
        front: calcFrom(hole.tee_lat, hole.tee_lng, hole.green_front_lat, hole.green_front_lng),
        center: calcFrom(hole.tee_lat, hole.tee_lng, hole.green_center_lat, hole.green_center_lng),
        back: calcFrom(hole.tee_lat, hole.tee_lng, hole.green_back_lat, hole.green_back_lng),
      });
    }
  }, [userPos, hole, currentHole, selectedCourse, selectedNearbyCourse]);

  const prevHole = () => setCurrentHole(h => Math.max(0, h - 1));
  const nextHole = () => setCurrentHole(h => Math.min(totalHoles - 1, h + 1));
  const recenter = () => { if (userPos && map.current) map.current.flyTo({ center: userPos, zoom: 17 }); };

  const totalHoles = selectedCourse?.holes?.length || 18;
  const courseName = selectedCourse?.name || selectedNearbyCourse?.clubName || 'Course';

  // Track shot handler
  const handleTrackShot = useCallback(async () => {
    if (!userPos || trackingShot) return;
    setTrackingShot(true);
    try {
      const { data } = await api.post('/shots', {
        session_id: sessionId,
        hole_number: currentHole + 1,
        lat: userPos[1],
        lng: userPos[0],
      });
      setTrackedShots(prev => [...prev, data]);

      // Add shot marker on map
      if (map.current) {
        const el = document.createElement('div');
        el.className = 'shot-marker';
        el.textContent = data.shot_number;
        const marker = new mapboxgl.Marker(el).setLngLat([userPos[0], userPos[1]]).addTo(map.current);
        shotMarkers.current.push(marker);
      }

      if (data.distance_from_prev) {
        toast.success(`Shot ${data.shot_number} tracked — ${data.distance_from_prev} yds from last`);
      } else {
        toast.success(`Shot ${data.shot_number} tracked`);
      }
    } catch {
      toast.error('Could not track shot');
    } finally {
      setTrackingShot(false);
    }
  }, [userPos, trackingShot, sessionId, currentHole]);

  if (!user) return <Navigate to="/auth" replace />;

  // Discovery mode - 18Birdies style course list
  if (mode === 'discovery') {
    return (
      <CourseDiscovery
        userPos={userPos}
        savedCourses={savedCourses}
        onSelectCourse={handleSelectNearbyCourse}
        onStartRound={startRound}
      />
    );
  }

  // Course Detail mode
  if (mode === 'detail') {
    const c = selectedNearbyCourse;
    const sc = courseDetail?.scorecard || [];
    return (
      <div className="min-h-screen bg-background" data-testid="course-detail-screen">
        <div className="max-w-md mx-auto px-4 pt-4 pb-24">
          <div className="flex items-center gap-3 mb-6">
            <button onClick={backToDiscovery} className="p-2 rounded-sm hover:bg-muted" data-testid="detail-back-btn">
              <ArrowLeft size={20} />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-sans font-bold text-foreground truncate">{c?.clubName || c?.courseName || 'Course'}</h1>
              <p className="text-xs text-muted-foreground font-sans">{c?.city}{c?.state ? `, ${c.state}` : ''}</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="bg-muted/30 rounded-sm p-3 text-center">
              <p className="text-[10px] text-muted-foreground font-sans uppercase tracking-wider">Holes</p>
              <p className="text-lg font-sans font-bold text-foreground">{c?.numHoles || 18}</p>
            </div>
            <div className="bg-muted/30 rounded-sm p-3 text-center">
              <p className="text-[10px] text-muted-foreground font-sans uppercase tracking-wider">Par</p>
              <p className="text-lg font-sans font-bold text-foreground">{c?.par || courseDetail?.par_total || '72'}</p>
            </div>
            <div className="bg-muted/30 rounded-sm p-3 text-center">
              <p className="text-[10px] text-muted-foreground font-sans uppercase tracking-wider">Type</p>
              <p className="text-sm font-sans font-semibold text-foreground capitalize">{c?.courseType || 'Public'}</p>
            </div>
          </div>
          {sc.length > 0 && (
            <div className="mb-6" data-testid="scorecard-section">
              <h2 className="text-sm font-sans font-bold text-foreground mb-3 uppercase tracking-wider">Scorecard</h2>
              <div className="overflow-x-auto rounded-sm border border-border">
                <table className="w-full text-xs font-sans">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-2 py-2 text-left text-muted-foreground">Hole</th>
                      {sc.map((h, i) => <th key={i} className="px-2 py-2 text-center min-w-[32px]">{i + 1}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-border">
                      <td className="px-2 py-2 text-muted-foreground font-semibold">Par</td>
                      {sc.map((h, i) => <td key={i} className="px-2 py-2 text-center font-semibold">{h.par || h.Par || '-'}</td>)}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <Button onClick={goToSetup} className="w-full rounded-sm bg-[#1B4332] hover:bg-[#0A4A1F] text-white font-sans font-bold py-3" data-testid="play-course-btn">
            <Golf size={18} weight="fill" className="mr-2" /> Play This Course
          </Button>
        </div>
      </div>
    );
  }

  // Play Setup mode
  if (mode === 'setup') {
    const addPlayer = () => setPlayers(prev => [...prev, { name: '', handicap: '' }]);
    const removePlayer = (idx) => setPlayers(prev => prev.filter((_, i) => i !== idx));
    const updatePlayer = (idx, field, val) => setPlayers(prev => prev.map((p, i) => i === idx ? { ...p, [field]: val } : p));
    const c = selectedNearbyCourse;
    return (
      <div className="min-h-screen bg-background" data-testid="play-setup-screen">
        <div className="max-w-md mx-auto px-4 pt-4 pb-24">
          <div className="flex items-center gap-3 mb-6">
            <button onClick={() => setMode('detail')} className="p-2 rounded-sm hover:bg-muted" data-testid="setup-back-btn">
              <ArrowLeft size={20} />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-sans font-bold text-foreground">Round Setup</h1>
              <p className="text-xs text-muted-foreground font-sans truncate">{c?.clubName}</p>
            </div>
          </div>
          <div className="mb-8" data-testid="round-type-selector">
            <h2 className="text-sm font-sans font-bold text-foreground mb-3 uppercase tracking-wider">Round Type</h2>
            <div className="grid grid-cols-3 gap-2">
              {[{ value: '18', label: '18 Holes' }, { value: 'front9', label: 'Front 9' }, { value: 'back9', label: 'Back 9' }].map(opt => (
                <button key={opt.value} onClick={() => setRoundType(opt.value)} className={`py-3 px-2 rounded-sm text-sm font-sans font-semibold border transition-colors ${roundType === opt.value ? 'bg-[#1B4332] text-white border-[#1B4332]' : 'bg-white text-foreground border-border hover:border-[#1B4332]/30'}`} data-testid={`round-type-${opt.value}`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="mb-8" data-testid="players-section">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-sans font-bold text-foreground uppercase tracking-wider">Players</h2>
              <button onClick={addPlayer} className="text-xs font-sans font-semibold text-[#1B4332]" data-testid="add-player-btn">+ Add Player</button>
            </div>
            <div className="space-y-3">
              {players.map((p, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`player-row-${i}`}>
                  <div className="w-7 h-7 rounded-full bg-[#1B4332] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</div>
                  <input type="text" placeholder="Player name" value={p.name} onChange={e => updatePlayer(i, 'name', e.target.value)} className="flex-1 px-3 py-2 border border-border rounded-sm text-sm font-sans bg-background" data-testid={`player-name-${i}`} />
                  <input type="number" placeholder="HCP" value={p.handicap} onChange={e => updatePlayer(i, 'handicap', e.target.value)} className="w-16 px-2 py-2 border border-border rounded-sm text-sm font-sans bg-background text-center" data-testid={`player-hcp-${i}`} />
                  {players.length > 1 && <button onClick={() => removePlayer(i)} className="text-destructive/60 hover:text-destructive p-1"><CaretLeft size={16} /></button>}
                </div>
              ))}
            </div>
          </div>
          <Button onClick={startPlayRound} className="w-full rounded-sm bg-[#1B4332] hover:bg-[#0A4A1F] text-white font-sans font-bold py-3" data-testid="start-round-btn">
            <Flag size={18} weight="fill" className="mr-2" /> Start Round
          </Button>
        </div>
      </div>
    );
  }

  // Pin-drop course builder mode
  if (mode === 'pindrop') {
    const currentPinHole = customHoles.length + 1;
    const addHolePin = (type) => {
      if (!userPos) { toast.error('GPS position required'); return; }
      const updatedHoles = [...customHoles];
      const holeIdx = updatedHoles.findIndex(h => h.hole_number === currentPinHole);
      const holeData = holeIdx >= 0 ? { ...updatedHoles[holeIdx] } : { hole_number: currentPinHole, par: 4 };
      if (type === 'tee') {
        holeData.tee_lat = userPos[1];
        holeData.tee_lng = userPos[0];
      } else if (type === 'front') {
        holeData.green_front_lat = userPos[1];
        holeData.green_front_lng = userPos[0];
      } else if (type === 'center') {
        holeData.green_center_lat = userPos[1];
        holeData.green_center_lng = userPos[0];
      } else if (type === 'back') {
        holeData.green_back_lat = userPos[1];
        holeData.green_back_lng = userPos[0];
      }
      if (holeIdx >= 0) updatedHoles[holeIdx] = holeData;
      else updatedHoles.push(holeData);
      setCustomHoles(updatedHoles);
      toast.success(`Hole ${currentPinHole} ${type} pin dropped`);
    };
    const nextPinHole = () => {
      const h = customHoles.find(h => h.hole_number === currentPinHole);
      if (!h || !h.tee_lat || !h.green_center_lat) {
        toast.error('Drop at least tee and center green pins');
        return;
      }
      setCustomHoles(prev => [...prev.filter(ph => ph.hole_number !== currentPinHole + 1), { hole_number: currentPinHole + 1, par: 4 }].filter(h => h.hole_number <= 18));
    };
    const saveCustomCourse = async () => {
      if (!customCourseName.trim()) { toast.error('Enter course name'); return; }
      if (customHoles.length === 0) { toast.error('Add at least one hole'); return; }
      try {
        const { data } = await api.post('/golf/custom-course', {
          name: customCourseName,
          latitude: userPos?.[1],
          longitude: userPos?.[0],
          holes: customHoles,
          par: customHoles.reduce((s, h) => s + (h.par || 4), 0),
        });
        toast.success('Course saved!');
        setSelectedCourse(data);
        setMode('gps');
      } catch { toast.error('Could not save course'); }
    };
    const submitToOpenGolf = async () => {
      try {
        await api.post('/golf/submit-to-opengolf', {
          name: customCourseName,
          latitude: userPos?.[1],
          longitude: userPos?.[0],
          holes: customHoles,
        });
        toast.success('Submitted for review!');
      } catch { toast.error('Submission failed'); }
    };

    return (
      <div className="min-h-screen bg-background" data-testid="pindrop-screen">
        <div className="max-w-md mx-auto px-4 pt-4 pb-24">
          <div className="flex items-center gap-3 mb-6">
            <button onClick={backToDiscovery} className="p-2 rounded-sm hover:bg-muted" data-testid="pindrop-back-btn">
              <ArrowLeft size={20} />
            </button>
            <div className="flex-1">
              <h1 className="text-lg font-sans font-bold text-foreground">Add Your Course</h1>
              <p className="text-xs text-muted-foreground font-sans">Drop pins at your current GPS position</p>
            </div>
          </div>

          {/* Course name */}
          <input
            type="text"
            placeholder="Course name"
            value={customCourseName}
            onChange={e => setCustomCourseName(e.target.value)}
            className="w-full px-4 py-3 border border-border rounded-sm text-sm font-sans mb-6"
            data-testid="custom-course-name"
          />

          {/* Current position */}
          <div className="bg-muted/30 rounded-sm p-3 mb-4 text-center">
            <p className="text-[10px] text-muted-foreground font-sans uppercase tracking-wider mb-1">Your GPS Position</p>
            <p className="text-sm font-sans font-mono text-foreground">
              {userPos ? `${userPos[1].toFixed(6)}, ${userPos[0].toFixed(6)}` : 'Locating...'}
            </p>
          </div>

          {/* Hole pin-drop controls */}
          <div className="mb-6" data-testid="pindrop-controls">
            <h2 className="text-sm font-sans font-bold text-foreground mb-3">Hole {currentPinHole} — Drop Pins</h2>
            <p className="text-xs text-muted-foreground font-sans mb-3">Walk to each position and tap to drop a pin</p>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => addHolePin('tee')} className="py-3 px-3 rounded-sm border border-border text-sm font-sans font-semibold hover:bg-muted/30 flex items-center justify-center gap-2" data-testid="pin-tee">
                <div className="w-4 h-4 rounded-full bg-white border-2 border-foreground" /> Tee Box
              </button>
              <button onClick={() => addHolePin('front')} className="py-3 px-3 rounded-sm border border-border text-sm font-sans font-semibold hover:bg-muted/30 flex items-center justify-center gap-2" data-testid="pin-front">
                <div className="w-4 h-4 rounded-full bg-green-500" /> Front Green
              </button>
              <button onClick={() => addHolePin('center')} className="py-3 px-3 rounded-sm border border-border text-sm font-sans font-semibold hover:bg-muted/30 flex items-center justify-center gap-2" data-testid="pin-center">
                <div className="w-4 h-4 rounded-full bg-[#D4AF37]" /> Center Green
              </button>
              <button onClick={() => addHolePin('back')} className="py-3 px-3 rounded-sm border border-border text-sm font-sans font-semibold hover:bg-muted/30 flex items-center justify-center gap-2" data-testid="pin-back">
                <div className="w-4 h-4 rounded-full bg-red-500" /> Back Green
              </button>
            </div>
            {currentPinHole < 18 && (
              <button onClick={nextPinHole} className="w-full mt-3 py-2 text-sm font-sans font-semibold text-[#1B4332] border border-[#1B4332]/30 rounded-sm hover:bg-[#1B4332]/5" data-testid="next-hole-pin">
                Next Hole →
              </button>
            )}
          </div>

          {/* Holes summary */}
          {customHoles.length > 0 && (
            <div className="mb-6 p-3 bg-muted/20 rounded-sm" data-testid="pindrop-summary">
              <p className="text-xs font-sans font-bold text-foreground mb-2">{customHoles.length} hole{customHoles.length > 1 ? 's' : ''} mapped</p>
              <div className="flex flex-wrap gap-1">
                {customHoles.map(h => (
                  <span key={h.hole_number} className={`px-2 py-0.5 rounded-sm text-[10px] font-sans font-bold ${h.tee_lat && h.green_center_lat ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                    #{h.hole_number}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-2">
            <Button onClick={saveCustomCourse} className="w-full rounded-sm bg-[#1B4332] hover:bg-[#0A4A1F] text-white font-sans font-bold py-3" data-testid="save-custom-course">
              Save & Play
            </Button>
            {customHoles.length > 0 && (
              <button onClick={submitToOpenGolf} className="w-full py-2 text-xs font-sans text-muted-foreground hover:text-foreground" data-testid="submit-opengolf-btn">
                Submit to OpenGolfAPI (Community)
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // GPS mode

  const lastShot = trackedShots.length > 0 ? trackedShots[trackedShots.length - 1] : null;
  const clubSuggestion = suggestClub(distances.center);
  const dynamicSuggestion = suggestClubDynamic(distances.center, clubStats);

  return (
    <div className="w-screen h-screen flex flex-col relative" style={{ background: 'transparent', pointerEvents: 'none' }} data-testid="play-gps-page">
      {/* ===== MAP (full bleed) ===== */}
      <div ref={mapContainer} className="flex-grow min-h-0 w-full h-full relative" style={{ zIndex: 1, pointerEvents: 'auto' }} data-testid="mapbox-container" />

      {/* ===== TOP ROW: Back + Distance Badge + Icons ===== */}
      <div className="absolute top-12 left-3 right-3 safe-area-top flex items-start gap-2" style={{ zIndex: 999 }}>
        {/* Back */}
        <button onClick={backToDiscovery} className="gps-icon-btn flex-shrink-0" data-testid="back-to-discovery-btn">
          <ArrowLeft size={18} weight="bold" className="text-white" />
        </button>

        {/* Distance Badge — Front / Middle / Back */}
        <div className="flex-1 min-w-0" data-testid="distance-badge">
          <div className="gps-distance-bar">
            <div className="flex-1 text-center">
              <p className="text-[9px] text-emerald-400 uppercase tracking-wider font-sans font-bold">Front</p>
              <p className="text-white text-xl font-sans font-bold leading-tight" data-testid="distance-front-value">
                {distances.front ?? '—'}
              </p>
            </div>
            <div className="gps-bar-divider" />
            <div className="flex-1 text-center">
              <p className="text-[9px] text-[#D4AF37] uppercase tracking-wider font-sans font-bold">Middle</p>
              <p className="text-white text-2xl font-sans font-extrabold leading-tight" data-testid="distance-center-value">
                {distances.center ?? '—'}
              </p>
            </div>
            <div className="gps-bar-divider" />
            <div className="flex-1 text-center">
              <p className="text-[9px] text-red-400 uppercase tracking-wider font-sans font-bold">Back</p>
              <p className="text-white text-xl font-sans font-bold leading-tight" data-testid="distance-back-value">
                {distances.back ?? '—'}
              </p>
            </div>
          </div>
          <p className="text-center text-[9px] text-white/40 font-sans mt-1" data-testid="distance-source-label">
            {userPos ? 'yds from current position' : hole ? 'yds from tee box' : ''}
          </p>
        </div>

        {/* Recenter + Scorecard */}
        <div className="flex flex-col gap-2 flex-shrink-0">
          <button onClick={recenter} className="gps-icon-btn" data-testid="recenter-btn">
            <Crosshair size={18} weight="bold" className="text-white" />
          </button>
          <button onClick={() => setShowScorecard(true)} className="gps-icon-btn" data-testid="scorecard-float-btn">
            <ListBullets size={18} weight="bold" className="text-[#D4AF37]" />
          </button>
        </div>
      </div>

      {/* ===== LAST SHOT PILL ===== */}
      {lastShot && (
        <div className="absolute top-[100px] left-1/2 -translate-x-1/2" style={{ zIndex: 999 }} data-testid="last-shot-info">
          <div className="gps-hud-pill-sm">
            <Path size={12} weight="bold" className="text-white/50" />
            <span className="text-white/70 font-sans text-xs">
              Shot {lastShot.shot_number}{lastShot.distance_from_prev ? ` · ${lastShot.distance_from_prev} yds` : ''}
            </span>
          </div>
        </div>
      )}

      {/* ===== CLUB SUGGESTION OVERLAY ===== */}
      {distances.center && (clubSuggestion || dynamicSuggestion) && (
        <div className="absolute left-1/2 -translate-x-1/2" style={{ zIndex: 999, top: lastShot ? '130px' : '100px' }} data-testid="club-suggestion">
          <div className="gps-hud-pill-sm">
            <Golf size={12} weight="bold" className="text-[#D4AF37]" />
            {dynamicSuggestion ? (
              <>
                <span className="text-[#D4AF37] font-sans text-xs font-bold">{dynamicSuggestion.abbr}</span>
                <span className="text-white/50 font-sans text-[10px]">avg {dynamicSuggestion.avg}y</span>
              </>
            ) : (
              <>
                <span className="text-[#D4AF37] font-sans text-xs font-bold">{clubSuggestion}</span>
                <span className="text-white/50 font-sans text-[10px]">suggested</span>
              </>
            )}
          </div>
        </div>
      )}

      {/* ===== BOTTOM: Track Shot + Hole Nav ===== */}
      <div className="absolute bottom-4 left-3 right-3 safe-area-bottom flex flex-col gap-2" style={{ zIndex: 999 }}>
        {/* Track Shot */}
        <div className="gps-bottom-bar">
          <button
            onClick={handleTrackShot}
            disabled={trackingShot || !userPos}
            className="gps-track-btn disabled:opacity-30"
            data-testid="track-shot-btn"
          >
            {trackingShot ? (
              <div className="w-5 h-5 rounded-full" style={{ borderWidth: 2, borderStyle: 'solid', borderColor: '#0B1F14', borderTopColor: 'transparent', animation: 'spin 0.6s linear infinite' }} />
            ) : (
              <Target size={20} weight="bold" />
            )}
            <span className="font-sans font-bold text-sm uppercase tracking-wider">
              {trackingShot ? 'Tracking' : 'Track Shot'}
            </span>
          </button>
        </div>

        {/* Hole Navigation: Prev | Hole X | Next */}
        <div className="gps-bottom-bar">
          <button
            onClick={prevHole}
            disabled={currentHole === 0}
            className="gps-footer-btn disabled:opacity-20"
            data-testid="prev-hole-btn"
          >
            <CaretLeft size={22} weight="bold" className="text-white" />
          </button>

          <div className="flex-1 text-center" data-testid="current-hole-number">
            <span className="text-white font-sans font-bold text-base">Hole {currentHole + 1}</span>
            {hole && (
              <span className="text-white/40 font-sans text-xs ml-2">Par {hole.par}</span>
            )}
          </div>

          <button
            onClick={nextHole}
            disabled={currentHole >= totalHoles - 1}
            className="gps-footer-btn disabled:opacity-20"
            data-testid="next-hole-btn"
          >
            <CaretRight size={22} weight="bold" className="text-white" />
          </button>
        </div>
      </div>

      {/* ===== SCORECARD MODAL ===== */}
      <GpsScorecard
        open={showScorecard}
        onClose={() => setShowScorecard(false)}
        currentHole={currentHole}
        onNavigateHole={(idx) => { setCurrentHole(idx); setShowScorecard(false); }}
        totalHoles={totalHoles}
        courseName={courseName}
      />
    </div>
  );
}
